// CardFaceRenderer (docs/07 §9): every card face — hand, table, showcase — is composed here with
// Canvas2D from content data, so the DOM cards and the 3D table cards are pixel-identical.
// Masters are 600×840 (portrait) / 840×600 (City Problems); illustrations come from
// public/assets/cards (tools/art/cards.py) and fall back to a painted school placeholder.
import * as THREE from 'three';
import { COLORS, type Color, type DefId, type IdentityDef, type PermitBag } from '@bcm/rules';
import { C, SCHOOL_NAME } from '../game/content';
import { LOCATION_ICON, drawIcon, resolutionIcon, type IconName } from '../game/icons';

export const CARD_W = 600;
export const CARD_H = 840;
export const PROBLEM_W = 840;
export const PROBLEM_H = 600;
/** Back-compat aliases used by the table layout. */
export const SPELL_W = CARD_W;
export const SPELL_H = CARD_H;

const INK = '#2a1f16';
const INK_SOFT = '#6a5541';
const CINZEL = '"Cinzel", "Cormorant Garamond", Georgia, serif';
const DISPLAY = '"Cormorant Garamond", Georgia, serif';
const BODY = '"Alegreya Sans", system-ui, sans-serif';
const STAMP = '"Special Elite", "Courier New", monospace';
/** Timing / condition phrases: one colour on every card (school colours are only for school names). */
const TIMING = '#7a1f2b';
const PERMIT_HEX: Record<Color, string> = { red: '#d0483c', yellow: '#e2b53e', blue: '#3f7fe0', green: '#3fa86a' };

type School = 'conjuration' | 'transmutation' | 'evocation' | 'divination';
const SCHOOL: Record<School, { light: string; mid: string; dark: string; glow: string; icon: IconName }> = {
  conjuration: { light: '#e0634f', mid: '#a82d28', dark: '#4e0f10', glow: '#ff8a5c', icon: 'flame' },
  transmutation: { light: '#f1cb62', mid: '#b98a22', dark: '#4f3508', glow: '#ffd970', icon: 'flask' },
  evocation: { light: '#6ea6f2', mid: '#2f5fb8', dark: '#0f2250', glow: '#8fc4ff', icon: 'bolt' },
  divination: { light: '#6fd29a', mid: '#2b8a55', dark: '#0d3a22', glow: '#9fffd0', icon: 'eye' },
};

// ─── Illustrations ───────────────────────────────────────────────────────────

const art = new Map<string, HTMLImageElement | null>();

function slug(def: string): string {
  return def.replace(/^[a-z]+\./, '').replace(/[^a-z0-9_]/g, '');
}

/** Preload card illustrations (missing files are fine: the renderer paints a placeholder). */
export function loadCardArt(defs: string[]): Promise<void> {
  const base = `${import.meta.env.BASE_URL}assets/cards/`;
  return Promise.all(
    defs.map(
      (d) =>
        new Promise<void>((resolve) => {
          if (art.has(d)) return resolve();
          const img = new Image();
          img.decoding = 'async';
          img.onload = () => {
            art.set(d, img);
            resolve();
          };
          img.onerror = () => {
            art.set(d, null);
            resolve();
          };
          img.src = `${base}${slug(d)}.webp`;
        }),
    ),
  ).then(() => undefined);
}

// ─── Canvas helpers ──────────────────────────────────────────────────────────

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function rr(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number | number[]): void {
  g.beginPath();
  g.roundRect(x, y, w, h, r);
}

let grainTile: HTMLCanvasElement | null = null;
function grain(): HTMLCanvasElement {
  if (grainTile) return grainTile;
  const [c, g] = canvas(128, 128);
  const img = g.createImageData(128, 128);
  let s = 1234567;
  for (let i = 0; i < img.data.length; i += 4) {
    s = (s * 16807) % 2147483647;
    const v = (s / 2147483647) * 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 22;
  }
  g.putImageData(img, 0, 0);
  grainTile = c;
  return c;
}

function paperFill(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, a = '#f6ecd2', b = '#e4d2a8'): void {
  g.save();
  rr(g, x, y, w, h, r);
  g.clip();
  const grad = g.createLinearGradient(x, y, x + w * 0.4, y + h);
  grad.addColorStop(0, a);
  grad.addColorStop(1, b);
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
  g.globalCompositeOperation = 'multiply';
  g.fillStyle = g.createPattern(grain(), 'repeat')!;
  g.fillRect(x, y, w, h);
  g.globalCompositeOperation = 'source-over';
  const v = g.createRadialGradient(x + w / 2, y + h / 2, Math.min(w, h) * 0.3, x + w / 2, y + h / 2, Math.max(w, h) * 0.75);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(90,60,20,0.22)');
  g.fillStyle = v;
  g.fillRect(x, y, w, h);
  g.restore();
}

/** The outer frame: card stock edge, coloured lacquer frame with a hatch, and a gilt hairline. */
function frame(g: CanvasRenderingContext2D, w: number, h: number, light: string, mid: string, dark: string): void {
  rr(g, 0, 0, w, h, 30);
  g.fillStyle = '#16100c';
  g.fill();
  g.save();
  rr(g, 6, 6, w - 12, h - 12, 26);
  g.clip();
  const grad = g.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, light);
  grad.addColorStop(0.45, mid);
  grad.addColorStop(1, dark);
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  g.strokeStyle = 'rgba(255,255,255,0.06)';
  g.lineWidth = 3;
  for (let i = -h; i < w; i += 14) {
    g.beginPath();
    g.moveTo(i, 0);
    g.lineTo(i + h, h);
    g.stroke();
  }
  g.restore();
  g.lineWidth = 3;
  g.strokeStyle = '#f0d58c';
  rr(g, 22, 22, w - 44, h - 44, 16);
  g.stroke();
  g.lineWidth = 1.5;
  g.strokeStyle = 'rgba(40,24,8,0.7)';
  rr(g, 26, 26, w - 52, h - 52, 14);
  g.stroke();
}

/** A gold-rimmed round medallion. */
function medallion(g: CanvasRenderingContext2D, x: number, y: number, r: number, fillA: string, fillB: string): void {
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.5)';
  g.shadowBlur = 12;
  g.shadowOffsetY = 4;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  const ring = g.createLinearGradient(x - r, y - r, x + r, y + r);
  ring.addColorStop(0, '#fff0bf');
  ring.addColorStop(0.5, '#b8893a');
  ring.addColorStop(1, '#f2d58c');
  g.fillStyle = ring;
  g.fill();
  g.restore();
  g.beginPath();
  g.arc(x, y, r - 7, 0, Math.PI * 2);
  const disc = g.createRadialGradient(x - r * 0.3, y - r * 0.35, 2, x, y, r);
  disc.addColorStop(0, fillA);
  disc.addColorStop(1, fillB);
  g.fillStyle = disc;
  g.fill();
}

/** A wax seal with an irregular rim (VP). */
/**
 * Victory points: the one gold coin used everywhere VP appears (the in-text VP glyph and the HUD's
 * VP badges share this gold), with the value in dark ink and a small "VP" beneath it.
 */
function vpCoin(g: CanvasRenderingContext2D, x: number, y: number, r: number, text: string): void {
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.55)';
  g.shadowBlur = 10;
  g.shadowOffsetY = 4;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = '#0b0f16';
  g.fill();
  g.restore();
  // Gilt rim, then the coin face.
  g.beginPath();
  g.arc(x, y, r - 2, 0, Math.PI * 2);
  g.fillStyle = '#c9a14e';
  g.fill();
  const face = g.createRadialGradient(x - r * 0.35, y - r * 0.4, 2, x, y, r);
  face.addColorStop(0, '#fff0bf');
  face.addColorStop(0.55, '#d8b05c');
  face.addColorStop(1, '#8a6a2c');
  g.beginPath();
  g.arc(x, y, r - 5, 0, Math.PI * 2);
  g.fillStyle = face;
  g.fill();
  g.beginPath();
  g.arc(x, y, r * 0.74, 0, Math.PI * 2);
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(90,60,20,0.45)';
  g.stroke();
  g.fillStyle = '#2a1d0c';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 ${Math.round(r * (text.length > 2 ? 0.72 : 0.9))}px ${CINZEL}`;
  g.fillText(text, x, y - r * 0.1);
  g.font = `700 ${Math.round(r * 0.26)}px ${CINZEL}`;
  g.fillText('VP', x, y + r * 0.48);
}

/** Resource glyph (same shapes as the DOM glyphs): colour + shape, never colour alone. */
export function drawGlyph(g: CanvasRenderingContext2D, kind: Color | 'mana' | 'vp', x: number, y: number, r: number): void {
  g.save();
  g.beginPath();
  if (kind === 'red') {
    g.moveTo(x, y - r);
    g.lineTo(x + r * 0.95, y + r * 0.8);
    g.lineTo(x - r * 0.95, y + r * 0.8);
  } else if (kind === 'yellow') {
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      g.lineTo(x + r * Math.cos(a), y + r * Math.sin(a));
    }
  } else if (kind === 'blue') {
    g.moveTo(x, y - r);
    g.lineTo(x + r, y);
    g.lineTo(x, y + r);
    g.lineTo(x - r, y);
  } else if (kind === 'green') {
    g.arc(x, y, r * 0.9, 0, Math.PI * 2);
  } else if (kind === 'mana') {
    g.moveTo(x, y - r);
    g.lineTo(x + r * 0.62, y - r * 0.15);
    g.lineTo(x, y + r);
    g.lineTo(x - r * 0.62, y - r * 0.15);
  } else {
    g.arc(x, y, r * 0.9, 0, Math.PI * 2);
  }
  g.closePath();
  if (kind === 'mana') {
    const m = g.createLinearGradient(x - r, y - r, x + r, y + r);
    m.addColorStop(0, '#ffe0f2');
    m.addColorStop(0.5, '#ec5fb3');
    m.addColorStop(1, '#8a1f64');
    g.fillStyle = m;
  } else if (kind === 'vp') {
    const m = g.createRadialGradient(x - r * 0.3, y - r * 0.3, 1, x, y, r);
    m.addColorStop(0, '#fff0bf');
    m.addColorStop(0.55, '#d8b05c');
    m.addColorStop(1, '#8a6a2c');
    g.fillStyle = m;
  } else g.fillStyle = PERMIT_HEX[kind];
  g.fill();
  g.lineWidth = Math.max(1.5, r * 0.12);
  g.strokeStyle = 'rgba(0,0,0,0.35)';
  g.stroke();
  g.restore();
}

/** A permit gem: glyph on a dark gilt-rimmed disc (the cost row on spells). */
function permitGem(g: CanvasRenderingContext2D, color: Color, x: number, y: number, r: number): void {
  medallion(g, x, y, r, '#2a2f3a', '#0c0f16');
  drawGlyph(g, color, x, y + (color === 'red' ? r * 0.08 : 0), r * 0.5);
}

const TOKEN: Record<string, Color | 'mana' | 'vp'> = { R: 'red', Y: 'yellow', B: 'blue', G: 'green', MANA: 'mana', VP: 'vp' };

// ─── Rules typography ────────────────────────────────────────────────────────
// Card text is auto-emphasised so the eye lands on what matters:
//   bold          → actions, amounts and game nouns  (Gain, 2, Action cards, the Archive)
//   bold + accent → timing and conditions            (Once per round, When you visit)
//   school colour → school names                     (Evocation spells)
// Authors can force emphasis in content with **bold**, ==key== and __timing__.

type Style = 'n' | 'b' | 'k' | 't' | `s:${School}`;

const RULES: [RegExp, Style][] = [
  [/\(End Game\)|At the start of each round|At the end of the game|at the end of the game|at game end|Once per (?:round|game|turn)|once per (?:round|game|turn)|as a free action|(?:When|Whenever|If|After) (?:you|a rival) (?:[a-z]+)|When cast|until the end of next round|this turn|this round|immediately|permanently|instead|ignoring its permit cost/g, 't'],
  // The name of the Decree a card enacts ("enact Open Records — …").
  [/(?<=[Ee]nact )[A-Z][\w'-]*(?: [A-Z][\w'-]*)*/g, 'k'],
  [/\b(?:Conjuration|Transmutation|Evocation|Divination)\b/g, 'b'],
  [/\b(?:the )?(?:Mana Conduit|Archive|Arcanium|Grand Auditor's Office|Grand Auditor|Planning Office|Department(?:'s)?s?)\b|\bAction cards?\b|\bCity Problems?\b|\b(?:temporary )?Assistants?\b|\bspells?\b|\bpermits?(?: of your choice| of any colou?r)?\b|\bAction hand limit\b|\bDecrees?\b|\bUnder Review\b/g, 'k'],
  [/\b(?:Gain|gain|Draw|draw|Discard|discard|Play|play|Cast|cast|Claim|claim|Buy|buy|Place|place|Pay|pay|Score|score|Swap|swap|Exchange|exchange|Resolve|resolve|Clear|clear|Reveal|reveal|Liquidate|Choose|choose|Take|take|Remove|remove|Return|return|Enact|enact|Repeal|repeal|Revoke|revoke|pays?|lose|costs?|less|fewer|additional|extra|more|lower)\b|\b\d+\+?/g, 'b'],
];

interface Seg {
  text?: string;
  glyph?: Color | 'mana' | 'vp';
  style: Style;
}

function styleSchool(word: string): Style | null {
  const m = /^(Conjuration|Transmutation|Evocation|Divination)/.exec(word);
  return m ? (`s:${m[1]!.toLowerCase()}` as Style) : null;
}

/** Split rules text into styled segments (explicit markup first, then the automatic rules). */
function segments(text: string): Seg[] {
  const out: Seg[] = [];
  const explicit = /\*\*(.+?)\*\*|==(.+?)==|__(.+?)__/g;
  const pushAuto = (t: string) => {
    for (const part of t.split(/(\{[A-Z]+\})/)) {
      if (!part) continue;
      const tok = /^\{([A-Z]+)\}$/.exec(part);
      if (tok && TOKEN[tok[1]!]) {
        out.push({ glyph: TOKEN[tok[1]!]!, style: 'n' });
        continue;
      }
      // Mark matches of each rule in priority order on a per-character style map.
      const styles: Style[] = Array.from({ length: part.length }, () => 'n');
      for (const [re, st] of RULES) {
        re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(part))) {
          for (let i = m.index; i < m.index + m[0].length; i++) if (styles[i] === 'n') styles[i] = st;
        }
      }
      let i = 0;
      while (i < part.length) {
        let j = i;
        while (j < part.length && styles[j] === styles[i]) j++;
        out.push({ text: part.slice(i, j), style: styles[i]! });
        i = j;
      }
    }
  };
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = explicit.exec(text))) {
    if (m.index > last) pushAuto(text.slice(last, m.index));
    out.push({ text: m[1] ?? m[2] ?? m[3], style: m[1] ? 'b' : m[2] ? 'k' : 't' });
    last = m.index + m[0].length;
  }
  if (last < text.length) pushAuto(text.slice(last));
  return out;
}

interface Run {
  text?: string;
  glyph?: Color | 'mana' | 'vp';
  style: Style;
  w: number;
}

function fontFor(style: Style, size: number): string {
  return style === 'n' ? `500 ${size}px ${BODY}` : `700 ${size}px ${BODY}`;
}

/** Lay out styled segments into lines that fit `maxW` (spaces keep their phrase's style). */
function layout(g: CanvasRenderingContext2D, text: string, maxW: number, size: number): Run[][] {
  const glyphW = size * 1.02;
  const words: Run[] = [];
  for (const seg of segments(text)) {
    if (seg.glyph) {
      words.push({ glyph: seg.glyph, style: 'n', w: glyphW });
      continue;
    }
    for (const piece of seg.text!.split(/(\s+)/)) {
      if (!piece) continue;
      const style = seg.style === 'b' ? (styleSchool(piece) ?? 'b') : seg.style;
      g.font = fontFor(style, size);
      words.push({ text: /^\s+$/.test(piece) ? ' ' : piece, style, w: g.measureText(/^\s+$/.test(piece) ? ' ' : piece).width });
    }
  }
  const lines: Run[][] = [[]];
  let lw = 0;
  for (let i = 0; i < words.length; i++) {
    const run = words[i]!;
    if (run.text === ' ') {
      if (lw > 0) {
        lines[lines.length - 1]!.push(run);
        lw += run.w;
      }
      continue;
    }
    // Keep punctuation glued to the word before it.
    if (lw + run.w > maxW && lw > 0 && !/^[.,;:)]/.test(run.text ?? '')) {
      const line = lines[lines.length - 1]!;
      while (line.length && line[line.length - 1]!.text === ' ') line.pop();
      lines.push([]);
      lw = 0;
    }
    lines[lines.length - 1]!.push(run);
    lw += run.w;
  }
  return lines;
}

/** Centred, auto-shrinking, emphasised rules text inside a box. Returns the bottom of the text. */
function rulesText(g: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, start = 31, min = 20): number {
  let size = start;
  let lines: Run[][] = [];
  let lineH = 0;
  for (; size >= min; size -= 1) {
    lines = layout(g, text, w, size);
    lineH = size * 1.2;
    if (lines.length * lineH <= h) break;
  }
  g.textBaseline = 'middle';
  g.textAlign = 'left';
  const total = lines.length * lineH;
  let cy = y + (h - total) / 2 + lineH / 2;
  for (const line of lines) {
    const lw = line.reduce((a, r) => a + r.w, 0);
    let cx = x + (w - lw) / 2;
    for (const r of line) {
      if (r.glyph) drawGlyph(g, r.glyph, cx + r.w / 2, cy, size * 0.44);
      else {
        g.font = fontFor(r.style, size);
        g.fillStyle = r.style.startsWith('s:') ? shadeHex(SCHOOL[r.style.slice(2) as School].mid, -0.22) : r.style === 't' ? TIMING : '#1e150e';
        if (r.text !== ' ') g.fillText(r.text!, cx, cy);
      }
      cx += r.w;
    }
    cy += lineH;
  }
  return y + (h + total) / 2;
}

function fitFont(g: CanvasRenderingContext2D, text: string, weight: string, family: string, start: number, maxW: number, min = 18): number {
  let size = start;
  g.font = `${weight} ${size}px ${family}`;
  while (g.measureText(text).width > maxW && size > min) {
    size -= 1;
    g.font = `${weight} ${size}px ${family}`;
  }
  return size;
}

/** Title on a ribbon, one or two lines, shrink-to-fit. */
function titleRibbon(g: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, dark: string, color = '#fbefd0'): void {
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.45)';
  g.shadowBlur = 10;
  g.shadowOffsetY = 3;
  g.beginPath();
  const notch = 16;
  g.moveTo(x, y);
  g.lineTo(x + w, y);
  g.lineTo(x + w - notch, y + h / 2);
  g.lineTo(x + w, y + h);
  g.lineTo(x, y + h);
  g.lineTo(x + notch, y + h / 2);
  g.closePath();
  const grad = g.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, shadeHex(dark, 0.18));
  grad.addColorStop(1, shadeHex(dark, -0.25));
  g.fillStyle = grad;
  g.fill();
  g.restore();
  g.lineWidth = 2;
  g.strokeStyle = '#e8c774';
  g.stroke();
  g.fillStyle = color;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const maxW = w - notch * 2 - 24;
  const one = fitFont(g, text, '700', CINZEL, Math.min(52, h * 0.5), maxW, Math.min(38, h * 0.4));
  if (g.measureText(text).width <= maxW) {
    g.font = `700 ${one}px ${CINZEL}`;
    g.fillText(text, x + w / 2, y + h / 2 + 2);
    return;
  }
  const words = text.split(' ');
  let best = 1;
  let bestW = Infinity;
  g.font = `700 ${Math.round(h * 0.36)}px ${CINZEL}`;
  for (let i = 1; i < words.length; i++) {
    const a = g.measureText(words.slice(0, i).join(' ')).width;
    const b = g.measureText(words.slice(i).join(' ')).width;
    if (Math.max(a, b) < bestW) {
      bestW = Math.max(a, b);
      best = i;
    }
  }
  const l1 = words.slice(0, best).join(' ');
  const l2 = words.slice(best).join(' ');
  const s = Math.min(fitFont(g, l1, '700', CINZEL, Math.round(h * 0.4), maxW, 20), fitFont(g, l2, '700', CINZEL, Math.round(h * 0.4), maxW, 20));
  g.font = `700 ${s}px ${CINZEL}`;
  g.fillText(l1, x + w / 2, y + h / 2 - s * 0.52);
  g.fillText(l2, x + w / 2, y + h / 2 + s * 0.56);
}

/** Illustration window with an inner shadow and gilt rim; paints a placeholder when no art exists. */
function artWindow(g: CanvasRenderingContext2D, def: string, x: number, y: number, w: number, h: number, glow: string, dark: string, icon: IconName): void {
  g.save();
  rr(g, x, y, w, h, 10);
  g.clip();
  const img = art.get(def);
  if (img) {
    const s = Math.max(w / img.width, h / img.height);
    g.drawImage(img, x + (w - img.width * s) / 2, y + (h - img.height * s) / 2, img.width * s, img.height * s);
  } else {
    const bg = g.createRadialGradient(x + w / 2, y + h * 0.55, 10, x + w / 2, y + h / 2, Math.max(w, h) * 0.75);
    bg.addColorStop(0, shadeHex(glow, -0.05));
    bg.addColorStop(0.45, shadeHex(dark, 0.15));
    bg.addColorStop(1, '#07060a');
    g.fillStyle = bg;
    g.fillRect(x, y, w, h);
    let s = def.length * 97 + def.charCodeAt(def.length - 1);
    const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 40; i++) {
      g.globalAlpha = 0.2 + rnd() * 0.6;
      g.fillStyle = shadeHex(glow, 0.5);
      g.beginPath();
      g.arc(x + rnd() * w, y + rnd() * h, 1 + rnd() * 3, 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;
    g.shadowColor = glow;
    g.shadowBlur = 40;
    drawIcon(g, icon, x + w / 2, y + h / 2, Math.min(w, h) * 0.55, shadeHex(glow, 0.55));
    g.shadowBlur = 0;
  }
  const inner = g.createLinearGradient(0, y, 0, y + h);
  inner.addColorStop(0, 'rgba(0,0,0,0.35)');
  inner.addColorStop(0.12, 'rgba(0,0,0,0)');
  inner.addColorStop(0.85, 'rgba(0,0,0,0)');
  inner.addColorStop(1, 'rgba(0,0,0,0.4)');
  g.fillStyle = inner;
  g.fillRect(x, y, w, h);
  g.restore();
  rr(g, x, y, w, h, 10);
  g.lineWidth = 4;
  g.strokeStyle = '#1b120b';
  g.stroke();
  rr(g, x - 3, y - 3, w + 6, h + 6, 12);
  g.lineWidth = 2;
  g.strokeStyle = '#e8c774';
  g.stroke();
}

function divider(g: CanvasRenderingContext2D, cx: number, y: number, w: number, color = '#a8844a'): void {
  g.strokeStyle = color;
  g.fillStyle = color;
  g.lineWidth = 1.5;
  g.beginPath();
  g.moveTo(cx - w / 2, y);
  g.lineTo(cx - 10, y);
  g.moveTo(cx + 10, y);
  g.lineTo(cx + w / 2, y);
  g.stroke();
  g.beginPath();
  g.moveTo(cx, y - 5);
  g.lineTo(cx + 6, y);
  g.lineTo(cx, y + 5);
  g.lineTo(cx - 6, y);
  g.closePath();
  g.fill();
}

function flavorText(g: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number): void {
  let size = 36;
  let lines: string[] = [];
  for (; size >= 24; size--) {
    g.font = `italic 600 ${size}px ${DISPLAY}`;
    lines = wrap(g, text, w);
    if (lines.length * size * 1.06 <= h) break;
  }
  g.fillStyle = '#56412e';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const lh = size * 1.06;
  const top = y + (h - lines.length * lh) / 2 + lh / 2;
  lines.forEach((l, i) => g.fillText(l, x + w / 2, top + i * lh));
}

function wrap(g: CanvasRenderingContext2D, text: string, w: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const t = line ? `${line} ${word}` : word;
    if (g.measureText(t).width > w && line) {
      out.push(line);
      line = word;
    } else line = t;
  }
  if (line) out.push(line);
  return out;
}

function shadeHex(hex: string, amt: number): string {
  const c = new THREE.Color(hex);
  c.lerp(new THREE.Color(amt >= 0 ? '#ffffff' : '#000000'), Math.abs(amt));
  return `#${c.getHexString()}`;
}

function footer(g: CanvasRenderingContext2D, w: number, y: number, left: string, center: string, color: string): void {
  g.textBaseline = 'middle';
  g.font = `700 22px ${CINZEL}`;
  g.fillStyle = color;
  g.textAlign = 'center';
  g.fillText(center.toUpperCase(), w / 2, y);
  g.font = `400 19px ${STAMP}`;
  g.fillStyle = 'rgba(42,31,22,0.55)';
  g.textAlign = 'left';
  g.fillText(left, 50, y);
}

// ─── Faces ───────────────────────────────────────────────────────────────────

const masters = new Map<string, HTMLCanvasElement>();
const textures = new Map<string, THREE.CanvasTexture>();

function master(key: string, draw: () => HTMLCanvasElement): HTMLCanvasElement {
  let c = masters.get(key);
  if (!c) {
    c = draw();
    masters.set(key, c);
  }
  return c;
}

export interface SpellValues {
  price: number;
  cost: PermitBag;
  vp: number;
}

/** The big rules panel: the most important thing on every card. */
function rulesPanel(g: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, start = 58, min = 30, flavor?: string, flavorShare = 0.31): void {
  rr(g, x, y, w, h, 14);
  g.fillStyle = 'rgba(255,251,240,0.78)';
  g.fill();
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(140,110,60,0.55)';
  g.stroke();
  // Flavour keeps a fixed, modest slot at the bottom; the rules get everything else.
  const fh = flavor ? Math.min(124, h * flavorShare) : 0;
  rulesText(g, text, x + 18, y + 12, w - 36, h - 24 - fh, start, min);
  if (flavor) {
    divider(g, x + w / 2, y + h - fh - 4, w * 0.5);
    flavorText(g, flavor.replace(/^["“]|["”]$/g, ''), x + 22, y + h - fh + 2, w - 44, fh - 10);
  }
}

/** A slim illustration strip (school colour + emblem, or art when present). */
function artStrip(g: CanvasRenderingContext2D, def: string, x: number, y: number, w: number, h: number, glow: string, dark: string, icon: IconName): void {
  artWindow(g, def, x, y, w, h, glow, dark, icon);
}

function drawSpell(def: DefId, v: SpellValues): HTMLCanvasElement {
  const sp = C.spells.get(def)!;
  const sc = SCHOOL[sp.school as School];
  const [c, g] = canvas(CARD_W, CARD_H);
  frame(g, CARD_W, CARD_H, sc.light, sc.mid, sc.dark);
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12);
  // 1 · Title, full width.
  titleRibbon(g, sp.name, 42, 44, CARD_W - 84, 104, sc.dark);
  // 2 · Cost row: price (mana) · permit gems · VP.
  artStrip(g, def, 50, 160, CARD_W - 100, 104, sc.glow, sc.dark, sc.icon);
  medallion(g, 92, 212, 54, '#3a1830', '#12060e');
  drawGlyph(g, 'mana', 92, 190, 14);
  g.fillStyle = '#ffe6f4';
  g.font = `700 46px ${CINZEL}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(String(v.price), 92, 228);
  vpCoin(g, CARD_W - 92, 212, 54, String(v.vp));
  const gems = COLORS.flatMap((col) => Array.from({ length: v.cost[col] ?? 0 }, () => col));
  // Gems share the space between the price medallion and the VP seal.
  const gr = Math.min(34, ((CARD_W - 320) / Math.max(1, gems.length) - 6) / 2);
  const gx0 = CARD_W / 2 - ((gems.length - 1) * (gr * 2 + 6)) / 2;
  gems.forEach((col, i) => permitGem(g, col, gx0 + i * (gr * 2 + 6), 212, gr));
  // 3 · Rules text: the biggest element on the card.
  rulesPanel(g, sp.text, 50, 282, CARD_W - 100, 474, 58, 30, sp.flavor);
  footer(g, CARD_W, 786, `FORM ${sp.formNo}`, SCHOOL_NAME[sp.school], shadeHex(sc.dark, 0.1));
  drawIcon(g, sc.icon, CARD_W - 64, 786, 26, shadeHex(sc.mid, -0.1));
  return c;
}

function drawAction(def: DefId): HTMLCanvasElement {
  const ac = C.actions.get(def)!;
  const [c, g] = canvas(CARD_W, CARD_H);
  frame(g, CARD_W, CARD_H, '#9a3a4e', '#5e1a2c', '#23070f');
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12, '#f4ecdc', '#dccdae');
  titleRibbon(g, ac.name, 42, 44, CARD_W - 84, 104, '#3a1020');
  artStrip(g, def, 50, 160, CARD_W - 100, 104, '#e8a0ff', '#2a0f33', 'stamp');
  g.save();
  g.translate(CARD_W / 2, 212);
  rr(g, -170, -24, 340, 48, 6);
  g.fillStyle = 'rgba(122,31,43,0.92)';
  g.fill();
  g.strokeStyle = '#e8c774';
  g.lineWidth = 2;
  g.stroke();
  g.fillStyle = '#fbe9d0';
  g.font = `400 28px ${STAMP}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText('ACTION REQUIRED', 0, 2);
  g.restore();
  rulesPanel(g, ac.text, 50, 282, CARD_W - 100, 474, 58, 30, ac.flavor);
  footer(g, CARD_W, 786, 'MEMO', 'Play at the Planning Office', '#5e1a2c');
  return c;
}

/**
 * A City Problem. With a Resolution in this game (P-17) the card makes room for a brass band under
 * the requirement: the Resolution's emblem, its name and its one-line rule, enacted for everyone
 * once the first claim comes in.
 */
function drawProblem(def: DefId, resolution?: DefId): HTMLCanvasElement {
  const pr = C.problems.get(def)!;
  const res = resolution ? C.resolutions.get(resolution) : undefined;
  const [c, g] = canvas(PROBLEM_W, PROBLEM_H);
  frame(g, PROBLEM_W, PROBLEM_H, '#7a5aa8', '#3f2a66', '#140b24');
  paperFill(g, 34, 34, PROBLEM_W - 68, PROBLEM_H - 68, 12, '#f2ead8', '#d8cbad');
  // Two layouts: the classic one, and a tighter one that fits the Resolution band.
  const L = res
    ? { title: [44, 80], label: 142, panel: [154, 146], share: 0.4, tiers: [432, 102], tierLabel: 24, tierVp: 70, vpSize: 48 }
    : { title: [44, 92], label: 160, panel: [176, 236], share: 0.44, tiers: [428, 110], tierLabel: 28, tierVp: 78, vpSize: 54 };
  titleRibbon(g, pr.name, 42, L.title[0]!, PROBLEM_W - 84, L.title[1]!, '#26173f');
  // Requirement: the rule, big.
  g.fillStyle = '#5b4380';
  g.font = `700 22px ${CINZEL}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText('REQUIREMENT', PROBLEM_W / 2, L.label);
  rulesPanel(g, pr.text, 50, L.panel[0]!, PROBLEM_W - 100, L.panel[1]!, res ? 46 : 52, 28, pr.description, L.share);
  if (res) resolutionBand(g, res.id, 50, 312, PROBLEM_W - 100, 110);
  // Rewards.
  const [ty, th] = L.tiers as [number, number];
  pr.tiers.forEach((vp, i) => {
    const w = (PROBLEM_W - 112) / 2;
    const x = 50 + i * (w + 12);
    rr(g, x, ty, w, th, 12);
    const grad = g.createLinearGradient(0, ty, 0, ty + th);
    grad.addColorStop(0, i === 0 ? '#f2d58c' : '#ddcfae');
    grad.addColorStop(1, i === 0 ? '#b8893a' : '#a8946a');
    g.fillStyle = grad;
    g.fill();
    g.strokeStyle = '#5a4020';
    g.lineWidth = 2;
    g.stroke();
    g.fillStyle = INK;
    g.textAlign = 'center';
    g.font = `700 24px ${CINZEL}`;
    // The Resolution comes with the first claim only: say so on that tier.
    g.fillText(i === 0 ? (res ? 'FIRST CLAIM · RESOLVES' : 'FIRST TO CLAIM') : 'SECOND TO CLAIM', x + w / 2, ty + L.tierLabel);
    g.font = `700 ${L.vpSize}px ${CINZEL}`;
    g.fillText(`${vp} VP`, x + w / 2, ty + L.tierVp);
  });
  return c;
}

/** Brass, engraved: the plaque a Resolution becomes. */
function brassFill(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const grad = g.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, '#f4dea0');
  grad.addColorStop(0.45, '#d9b567');
  grad.addColorStop(1, '#a8792e');
  g.fillStyle = grad;
  g.fill();
  // A faint brushed-metal sheen.
  g.save();
  g.clip();
  g.globalAlpha = 0.18;
  g.strokeStyle = '#fff6d6';
  g.lineWidth = 1;
  for (let i = 0; i < h; i += 5) {
    g.beginPath();
    g.moveTo(x, y + i);
    g.lineTo(x + w, y + i + 2);
    g.stroke();
  }
  g.restore();
}

/** The Resolution band on a City Problem: emblem, "RESOLUTION · name", and its one-line rule. */
function resolutionBand(g: CanvasRenderingContext2D, id: DefId, x: number, y: number, w: number, h: number): void {
  const res = C.resolutions.get(id)!;
  g.save();
  g.shadowColor = 'rgba(40,20,0,0.45)';
  g.shadowBlur = 8;
  g.shadowOffsetY = 3;
  rr(g, x, y, w, h, 14);
  brassFill(g, x, y, w, h);
  g.restore();
  rr(g, x, y, w, h, 14);
  g.lineWidth = 3;
  g.strokeStyle = '#5a3d12';
  g.stroke();
  rr(g, x + 6, y + 6, w - 12, h - 12, 10);
  g.lineWidth = 1.2;
  g.strokeStyle = 'rgba(90,61,18,0.55)';
  g.stroke();
  // Rivets.
  for (const [rx, ry] of [
    [x + 16, y + 16],
    [x + w - 16, y + 16],
    [x + 16, y + h - 16],
    [x + w - 16, y + h - 16],
  ] as const) {
    g.beginPath();
    g.arc(rx, ry, 3.2, 0, Math.PI * 2);
    g.fillStyle = '#6b4a18';
    g.fill();
  }
  const mx = x + 58;
  const my = y + h / 2;
  medallion(g, mx, my, 40, '#5a1420', '#1f070b');
  const room = res.onEnact.find((e) => e.op === 'openAnnex');
  const icon = room && room.op === 'openAnnex' ? LOCATION_ICON[room.location] : resolutionIcon(id);
  drawIcon(g, icon, mx, my, 40, '#f2d58c');
  const tx = x + 112;
  const tw = w - 112 - 26;
  g.textAlign = 'left';
  g.textBaseline = 'middle';
  g.fillStyle = '#6b4312';
  g.font = `700 16px ${CINZEL}`;
  const eyebrow = res.spice === 'wild' ? 'RESOLUTION · WILD · FOR EVERY PLAYER' : 'RESOLUTION · FOR EVERY PLAYER';
  (g as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '2px';
  g.fillText(eyebrow, tx, y + 24);
  (g as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '0px';
  g.fillStyle = '#2a1606';
  const size = fitFont(g, res.name, '700', CINZEL, 30, tw, 22);
  g.font = `700 ${size}px ${CINZEL}`;
  g.fillText(res.name, tx, y + 52);
  // The one-line rule, with glyphs, left-aligned under the name.
  g.save();
  g.translate(0, 0);
  rulesTextLeft(g, res.text, tx, y + 67, tw, 36, 28, 18);
  g.restore();
}

/** One or two lines of emphasised rules text, left-aligned in a box (the Resolution band). */
function rulesTextLeft(g: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, start: number, min: number): void {
  let size = start;
  let lines: Run[][] = [];
  for (; size >= min; size -= 1) {
    lines = layout(g, text, w, size);
    if (lines.length === 1 || lines.length * size * 1.15 <= h) break;
  }
  g.textBaseline = 'middle';
  g.textAlign = 'left';
  const lineH = size * 1.15;
  let cy = y + (h - lines.length * lineH) / 2 + lineH / 2;
  for (const line of lines) {
    let cx = x;
    for (const r of line) {
      if (r.glyph) drawGlyph(g, r.glyph, cx + r.w / 2, cy, size * 0.44);
      else {
        g.font = fontFor(r.style, size);
        g.fillStyle = r.style === 't' ? TIMING : '#2a1606';
        if (r.text !== ' ') g.fillText(r.text!, cx, cy);
      }
      cx += r.w;
    }
    cy += lineH;
  }
}

/**
 * A Resolution as a card of its own (previews, the enactment beat, the Resolutions rail): a brass
 * notice of the Grand Council — the one-line rule big, the precise rule under it.
 */
function drawResolution(def: DefId): HTMLCanvasElement {
  const res = C.resolutions.get(def)!;
  const [c, g] = canvas(CARD_W, CARD_H);
  frame(g, CARD_W, CARD_H, '#e2c27a', '#8a5a24', '#241306');
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12, '#f5ecd6', '#dccaa2');
  g.fillStyle = '#4a2c0c';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 28px ${CINZEL}`;
  g.fillText('CITY RESOLUTION', CARD_W / 2, 72);
  g.strokeStyle = 'rgba(74,44,12,0.75)';
  for (const [y, lw] of [
    [94, 3],
    [101, 1],
  ] as const) {
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(64, y);
    g.lineTo(CARD_W - 64, y);
    g.stroke();
  }
  g.font = `400 19px ${STAMP}`;
  g.fillStyle = 'rgba(74,44,12,0.72)';
  g.fillText(res.spice === 'wild' ? 'PASSED BY THE GRAND COUNCIL · WILD' : 'PASSED BY THE GRAND COUNCIL', CARD_W / 2, 120);
  titleRibbon(g, res.name, 42, 140, CARD_W - 84, 96, '#3d2208');
  const room = res.onEnact.find((e) => e.op === 'openAnnex');
  const icon = room && room.op === 'openAnnex' ? LOCATION_ICON[room.location] : resolutionIcon(def);
  // The emblem, struck on a brass plate.
  rr(g, 50, 250, CARD_W - 100, 96, 14);
  brassFill(g, 50, 250, CARD_W - 100, 96);
  rr(g, 50, 250, CARD_W - 100, 96, 14);
  g.lineWidth = 2.5;
  g.strokeStyle = '#5a3d12';
  g.stroke();
  medallion(g, CARD_W / 2, 298, 42, '#5a1420', '#1f070b');
  drawIcon(g, icon, CARD_W / 2, 298, 44, '#f2d58c');
  drawIcon(g, 'gavel', 104, 298, 34, '#6b4312');
  drawIcon(g, 'gavel', CARD_W - 104, 298, 34, '#6b4312');
  // The rule (big), then the precise rule (small), then the flavour.
  rr(g, 50, 360, CARD_W - 100, 396, 14);
  g.fillStyle = 'rgba(255,251,240,0.8)';
  g.fill();
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(140,110,60,0.55)';
  g.stroke();
  rulesText(g, res.text, 68, 368, CARD_W - 136, 104, 46, 30);
  divider(g, CARD_W / 2, 482, (CARD_W - 100) * 0.5);
  rulesText(g, res.rules, 70, 492, CARD_W - 140, 170, 24, 16);
  divider(g, CARD_W / 2, 672, (CARD_W - 100) * 0.35);
  flavorText(g, res.flavor, 72, 680, CARD_W - 144, 70);
  footer(g, CARD_W, 786, 'RESOLUTION', 'For every player', '#4a2c0c');
  return c;
}

function drawIdentity(def: DefId): HTMLCanvasElement {
  const id = C.identities.get(def)!;
  const unfiled = id.faction !== 'ministry';
  const [c, g] = canvas(CARD_W, CARD_H);
  if (unfiled) frame(g, CARD_W, CARD_H, '#5a4a6e', '#2a1f3a', '#0c0812');
  else frame(g, CARD_W, CARD_H, '#4a6a9a', '#1f3358', '#0a1224');
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12);
  titleRibbon(g, id.name, 42, 44, CARD_W - 84, 104, unfiled ? '#2a1f3a' : '#1b2d4d');
  artStrip(g, def, 50, 160, CARD_W - 100, 150, unfiled ? '#b58aff' : '#8fb8ff', unfiled ? '#1a1024' : '#0f1c33', 'quill');
  g.fillStyle = INK_SOFT;
  g.font = `italic 500 28px ${DISPLAY}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(`${id.role} · ${unfiled ? 'The Unfiled' : 'Ministry'}`, CARD_W / 2, 336);
  g.fillStyle = unfiled ? '#3b2c47' : '#2d4a5c';
  const s = fitFont(g, id.abilityName.toUpperCase(), '700', CINZEL, 36, CARD_W - 120, 24);
  g.font = `700 ${s}px ${CINZEL}`;
  g.fillText(id.abilityName.toUpperCase(), CARD_W / 2, 382);
  if (id.package) {
    rulesPanel(g, id.text, 50, 404, CARD_W - 100, 206, 46, 24);
    drawPackage(g, id.package, 50, 618, CARD_W - 100, 140, unfiled ? '#3b2c47' : '#2d4a5c');
  } else rulesPanel(g, id.text, 50, 410, CARD_W - 100, 346, 50, 26);
  footer(g, CARD_W, 786, 'DOSSIER', 'Bureaucromancer', unfiled ? '#3b2c47' : '#2d4a5c');
  return c;
}

/**
 * The starting package (P-15) on an identity card: its name, then what's in it as tokens — permits
 * (colour and shape), a quartered "any colour" permit for each one picked in the draft, Action
 * cards and mana.
 */
function drawPackage(g: CanvasRenderingContext2D, pkg: NonNullable<IdentityDef['package']>, x: number, y: number, w: number, h: number, accent: string): void {
  g.save();
  rr(g, x, y, w, h, 14);
  g.fillStyle = 'rgba(42,31,22,0.08)';
  g.fill();
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(42,31,22,0.3)';
  g.setLineDash([7, 5]);
  g.stroke();
  g.setLineDash([]);
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = accent;
  g.font = `700 24px ${CINZEL}`;
  g.fillText('STARTING PACKAGE', x + w / 2, y + 30);

  type Item = { kind: Color } | { kind: 'any' } | { kind: 'card' } | { kind: 'mana'; n: number };
  const items: Item[] = [];
  for (const c of ['red', 'yellow', 'blue', 'green'] as Color[]) for (let k = 0; k < (pkg.permits?.[c] ?? 0); k++) items.push({ kind: c });
  for (let k = 0; k < (pkg.choice ?? 0); k++) items.push({ kind: 'any' });
  for (let k = 0; k < (pkg.actions ?? 0); k++) items.push({ kind: 'card' });
  if (pkg.mana) items.push({ kind: 'mana', n: pkg.mana });
  const step = Math.min(74, (w - 30) / Math.max(1, items.length));
  const r = Math.min(30, step * 0.42);
  const cy = y + 88;
  items.forEach((it, i) => {
    const cx = x + w / 2 + (i - (items.length - 1) / 2) * step;
    if (it.kind === 'any') {
      // Any colour: a gem quartered in the four permit colours.
      medallion(g, cx, cy, r, '#2a2f3a', '#0c0f16');
      (['red', 'yellow', 'blue', 'green'] as Color[]).forEach((c, q) => {
        g.beginPath();
        g.moveTo(cx, cy);
        g.arc(cx, cy, r * 0.62, -Math.PI / 2 + (q * Math.PI) / 2, -Math.PI / 2 + ((q + 1) * Math.PI) / 2);
        g.closePath();
        g.fillStyle = PERMIT_HEX[c];
        g.fill();
      });
      g.fillStyle = '#fff6e0';
      g.font = `700 ${Math.round(r * 0.9)}px ${CINZEL}`;
      g.fillText('?', cx, cy + 1);
    } else if (it.kind === 'card') {
      // An Action card: a small green card with the stamp mark.
      const cw = r * 1.3;
      const ch = r * 1.8;
      rr(g, cx - cw / 2, cy - ch / 2, cw, ch, 5);
      g.fillStyle = '#8a2e42';
      g.fill();
      g.lineWidth = 2;
      g.strokeStyle = '#c9a14e';
      g.stroke();
      g.fillStyle = '#f3e6c4';
      g.font = `700 ${Math.round(r * 0.8)}px ${CINZEL}`;
      g.fillText('A', cx, cy + 1);
    } else if (it.kind === 'mana') {
      drawGlyph(g, 'mana', cx - r * 0.35, cy, r * 0.62);
      g.fillStyle = INK;
      g.font = `700 ${Math.round(r * 0.95)}px ${CINZEL}`;
      g.fillText(`${it.n}`, cx + r * 0.55, cy + 2);
    } else permitGem(g, it.kind, cx, cy, r);
  });
  g.restore();
}

function drawAgenda(def: DefId): HTMLCanvasElement {
  const ag = C.agendas.get(def)!;
  const [c, g] = canvas(CARD_W, CARD_H);
  frame(g, CARD_W, CARD_H, '#a0404a', '#5a1a24', '#1f070b');
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12, '#efe3c6', '#d6c29a');
  titleRibbon(g, ag.name, 42, 44, CARD_W - 84, 104, '#4a1420');
  g.fillStyle = '#5a1a24';
  g.font = `400 26px ${STAMP}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText('SECRET AGENDA · EYES ONLY', CARD_W / 2, 184);
  rulesPanel(g, ag.text, 50, 214, CARD_W - 100, 420, 56, 30);
  vpCoin(g, CARD_W / 2, 712, 62, `+${ag.vp}`);
  g.fillStyle = '#5a1a24';
  g.font = `700 22px ${CINZEL}`;
  g.fillText('VICTORY POINTS AT GAME END', CARD_W / 2, 792);
  return c;
}

/**
 * A Decree: a notice torn from the Ministry Gazette. Civic green and gilt, a newsprint masthead, and
 * the rule itself as the largest thing on the card.
 */
function drawDecree(def: DefId): HTMLCanvasElement {
  const dc = C.decrees.get(def)!;
  const [c, g] = canvas(CARD_W, CARD_H);
  frame(g, CARD_W, CARD_H, '#4f8a70', '#1f4a3a', '#07170f');
  paperFill(g, 34, 34, CARD_W - 68, CARD_H - 68, 12, '#f3eddc', '#d9cfb0');
  // Masthead.
  g.fillStyle = '#1f3d31';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 30px ${CINZEL}`;
  g.fillText('THE MINISTRY GAZETTE', CARD_W / 2, 76);
  g.strokeStyle = 'rgba(31,61,49,0.8)';
  for (const [y, lw] of [
    [100, 3],
    [107, 1],
  ] as const) {
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(64, y);
    g.lineTo(CARD_W - 64, y);
    g.stroke();
  }
  g.font = `400 20px ${STAMP}`;
  g.fillStyle = 'rgba(31,61,49,0.7)';
  g.fillText('BY ORDER OF THE GRAND COUNCIL', CARD_W / 2, 126);
  titleRibbon(g, dc.name, 42, 148, CARD_W - 84, 104, '#173a2d');
  // "DECREE" band with the Ministry seal.
  g.save();
  g.translate(CARD_W / 2, 290);
  rr(g, -120, -22, 240, 44, 6);
  g.fillStyle = 'rgba(31,74,58,0.95)';
  g.fill();
  g.strokeStyle = '#e8c774';
  g.lineWidth = 2;
  g.stroke();
  g.fillStyle = '#f6ecd2';
  g.font = `400 28px ${STAMP}`;
  g.fillText('DECREE', 0, 2);
  g.restore();
  drawIcon(g, 'scales', 104, 290, 30, '#1f4a3a');
  drawIcon(g, 'scales', CARD_W - 104, 290, 30, '#1f4a3a');
  rulesPanel(g, dc.text, 50, 330, CARD_W - 100, 426, 60, 30, dc.flavor, 0.3);
  footer(g, CARD_W, 786, 'NOTICE', 'In force until repealed', '#1f4a3a');
  return c;
}

export type BackKind = 'spell' | 'action' | 'problem' | 'identity' | 'agenda' | 'decree';
const BACKS: Record<BackKind, { a: string; b: string; label: string; icon: IconName; w: number; h: number }> = {
  spell: { a: '#1f3a6a', b: '#0b1630', label: 'Spells', icon: 'compass', w: CARD_W, h: CARD_H },
  // Red like the Action card fronts (the Decrees are the green ones).
  action: { a: '#8a2e42', b: '#2a0a12', label: 'Action Cards', icon: 'stamp', w: CARD_W, h: CARD_H },
  problem: { a: '#4a2f78', b: '#170d2c', label: 'City Problems', icon: 'scales', w: PROBLEM_W, h: PROBLEM_H },
  identity: { a: '#243a5a', b: '#0a1224', label: 'Bureaucromancers', icon: 'quill', w: CARD_W, h: CARD_H },
  agenda: { a: '#7a1f2b', b: '#2a070d', label: 'Secret Agendas', icon: 'scroll', w: CARD_W, h: CARD_H },
  decree: { a: '#2f6a55', b: '#0b2019', label: 'Decrees', icon: 'scroll', w: CARD_W, h: CARD_H },
};

function drawBack(kind: BackKind): HTMLCanvasElement {
  const b = BACKS[kind];
  const [c, g] = canvas(b.w, b.h);
  rr(g, 0, 0, b.w, b.h, 30);
  g.fillStyle = '#0d0a08';
  g.fill();
  g.save();
  rr(g, 6, 6, b.w - 12, b.h - 12, 26);
  g.clip();
  const grad = g.createRadialGradient(b.w / 2, b.h / 2, 20, b.w / 2, b.h / 2, Math.max(b.w, b.h) * 0.7);
  grad.addColorStop(0, b.a);
  grad.addColorStop(1, b.b);
  g.fillStyle = grad;
  g.fillRect(0, 0, b.w, b.h);
  g.globalCompositeOperation = 'overlay';
  g.fillStyle = g.createPattern(grain(), 'repeat')!;
  g.fillRect(0, 0, b.w, b.h);
  g.globalCompositeOperation = 'source-over';
  g.strokeStyle = 'rgba(232,199,116,0.14)';
  g.lineWidth = 1.2;
  for (let i = 0; i < 26; i++) {
    g.beginPath();
    g.ellipse(b.w / 2, b.h / 2, 40 + i * 14, 40 + i * 14 * (b.h / b.w), (i * Math.PI) / 26, 0, Math.PI * 2);
    g.stroke();
  }
  g.restore();
  g.lineWidth = 3;
  g.strokeStyle = '#e2c078';
  rr(g, 22, 22, b.w - 44, b.h - 44, 16);
  g.stroke();
  g.lineWidth = 1.5;
  rr(g, 32, 32, b.w - 64, b.h - 64, 12);
  g.stroke();
  for (const [x, y] of [
    [32, 32],
    [b.w - 32, 32],
    [32, b.h - 32],
    [b.w - 32, b.h - 32],
  ] as const) {
    g.fillStyle = '#f2d58c';
    g.beginPath();
    g.moveTo(x, y - 9);
    g.lineTo(x + 9, y);
    g.lineTo(x, y + 9);
    g.lineTo(x - 9, y);
    g.fill();
  }
  const cy = b.h / 2 - (kind === 'problem' ? 30 : 50);
  g.shadowColor = 'rgba(242,213,140,0.5)';
  g.shadowBlur = 30;
  drawIcon(g, b.icon, b.w / 2, cy, kind === 'problem' ? 170 : 220, '#e8c774');
  g.shadowBlur = 0;
  g.fillStyle = '#f2d58c';
  g.font = `700 ${kind === 'problem' ? 40 : 46}px ${CINZEL}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(b.label.toUpperCase(), b.w / 2, cy + (kind === 'problem' ? 140 : 190));
  return c;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export type FaceKind = 'spell' | 'action' | 'problem' | 'identity' | 'agenda' | 'decree' | 'resolution';

export function faceKind(def: DefId): FaceKind | null {
  if (C.spells.has(def)) return 'spell';
  if (C.actions.has(def)) return 'action';
  if (C.problems.has(def)) return 'problem';
  if (C.identities.has(def)) return 'identity';
  if (C.agendas.has(def)) return 'agenda';
  if (C.decrees.has(def)) return 'decree';
  if (C.resolutions.has(def)) return 'resolution';
  return null;
}

/**
 * Which Resolution a City Problem carries in the current game (P-17): the table is the source of
 * truth, so the client registers a lookup (the presented view). Absent: the classic card.
 */
let resolutionOf: (problem: DefId) => DefId | undefined = () => undefined;
export function setProblemResolutionLookup(fn: (problem: DefId) => DefId | undefined): void {
  resolutionOf = fn;
}
export const problemResolutionOf = (problem: DefId): DefId | undefined => resolutionOf(problem);

/** The master canvas for a card face (cached). `values` only matters for spells. */
export function faceCanvas(def: DefId, values?: SpellValues): HTMLCanvasElement | null {
  const kind = faceKind(def);
  switch (kind) {
    case 'spell': {
      const sp = C.spells.get(def)!;
      const v = values ?? { price: sp.price, cost: sp.cost, vp: sp.vp };
      return master(`spell:${def}:${v.price}:${v.vp}:${JSON.stringify(v.cost)}`, () => drawSpell(def, v));
    }
    case 'action':
      return master(`action:${def}`, () => drawAction(def));
    case 'problem': {
      const res = resolutionOf(def);
      return master(`problem:${def}:${res ?? ''}`, () => drawProblem(def, res));
    }
    case 'resolution':
      return master(`resolution:${def}`, () => drawResolution(def));
    case 'identity':
      return master(`identity:${def}`, () => drawIdentity(def));
    case 'agenda':
      return master(`agenda:${def}`, () => drawAgenda(def));
    case 'decree':
      return master(`decree:${def}`, () => drawDecree(def));
    default:
      return null;
  }
}

export function backCanvas(kind: BackKind): HTMLCanvasElement {
  return master(`back:${kind}`, () => drawBack(kind));
}

/**
 * Sample a card material's map with a negative mip bias: the card faces are dense type, and plain
 * trilinear filtering picks a mip level that is visibly soft at normal table zoom. Anisotropic
 * filtering plus a ~0.7 level bias keeps the text crisp without shimmering.
 */
export function crispCard<M extends THREE.Material>(mat: M, bias = -0.7): M {
  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#ifdef USE_MAP
        diffuseColor *= texture2D( map, vMapUv, ${bias.toFixed(2)} );
      #endif`,
    );
  };
  mat.customProgramCacheKey = () => `crisp${bias}`;
  return mat;
}

function toTexture(key: string, c: HTMLCanvasElement): THREE.CanvasTexture {
  let t = textures.get(key);
  if (!t) {
    t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 16;
    t.generateMipmaps = true;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    textures.set(key, t);
  }
  return t;
}

export function spellFace(def: DefId, values: SpellValues): THREE.CanvasTexture {
  return toTexture(`spell:${def}:${values.price}:${values.vp}:${JSON.stringify(values.cost)}`, faceCanvas(def, values)!);
}

export function problemFace(def: DefId): THREE.CanvasTexture {
  return toTexture(`problem:${def}:${resolutionOf(def) ?? ''}`, faceCanvas(def)!);
}

/** Texture of any card face (used for the face-up discard pile). */
export function faceTexture(def: DefId): THREE.CanvasTexture | null {
  const c = faceCanvas(def);
  return c ? toTexture(`face:${def}`, c) : null;
}

export function cardBack(kind: BackKind): THREE.CanvasTexture {
  return toTexture(`back:${kind}`, backCanvas(kind));
}

export function disposeCardFaces(): void {
  for (const t of textures.values()) t.dispose();
  textures.clear();
}
